import { apiFetch } from "../../apiClient";
import { withLangfuseMetadataHeader } from "../../services/langfuseTrace";
import { isAbortError } from "../../utils/abortError";
import { checkAuthStatus } from "../../utils/authStatus";

export const TABULAR_METADATA_REQUEST_TIMEOUT_MS = 40_000;
export const TABULAR_EXECUTION_REQUEST_TIMEOUT_MS = 320_000;

const TABULAR_QUERY_BASE_PATH = "/api/finster/tabular-query-execution";
const MAX_PRECOMPUTED_FUNCTION_CALLS = 64;
const MAX_BATCH_RESULTS = 15;
const MAX_CELL_BATCH_RESULTS = 20;
const MAX_CITATIONS_PER_RESULT = 1_000;

export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

export type TabularResponseType =
  | "TEXT"
  | "NUMBER"
  | "DATE"
  | "BOOLEAN"
  | "SINGLE_SELECT"
  | "MULTI_SELECT";

export type TabularDataFormat =
  | { formatType: "auto" }
  | { formatType: "number"; unit?: string; decimalPlaces?: number }
  | {
      formatType: "currency";
      currency?: string;
      unit?: string;
      decimalPlaces?: number;
    }
  | { formatType: "percentage"; decimalPlaces?: number }
  | { formatType: "date"; dateFormat?: string }
  | { formatType: "template"; template: string };

export interface TabularCompanyTarget {
  company: {
    companyName: string;
    companyId?: string;
    companyTicker?: string;
    companyIsin?: string;
    userFileIds?: string[];
    directUserFileIds?: string[];
  };
}

export interface TabularDocumentTarget {
  document: {
    rowLabel: string;
    userFileIds?: string[];
    directUserFileIds?: string[];
  };
}

export type TabularTarget = TabularCompanyTarget | TabularDocumentTarget;

export interface PrecomputedFunctionCall {
  functionName: string;
  functionParameters: Record<string, JsonValue>;
  functionCallId?: string;
  stage?: 0 | 1 | 2;
}

/**
 * Resolved guidance for user-authored Skills, sent alongside `prompt` rather
 * than merged into it. The raw prompt still drives retrieval parameters, the
 * grid, and the response, so only the interpreting model sees this text, and it
 * is preserved byte-for-byte because the resolver composed it.
 */
export interface SelectedSkillsGuidanceRequestField {
  selectedSkillsGuidance?: string;
}

export interface ResolveDataFormatRequest
  extends SelectedSkillsGuidanceRequestField {
  prompt: string;
  responseType: TabularResponseType;
  dataSources?: string[];
}

export interface ResolveDataFormatResponse {
  dataFormat: TabularDataFormat;
  cleanedPrompt?: string;
}

export interface TabularQueryOptions
  extends SelectedSkillsGuidanceRequestField {
  prompt: string;
  responseType: TabularResponseType;
  dataSources?: string[];
  tagOptions?: string[];
  noMatchValue?: string;
  dataFormat?: TabularDataFormat;
}

export interface GetFunctionCallMetadataRequest extends TabularQueryOptions {
  targets: TabularTarget[];
}

export interface GetFunctionCallMetadataResponse {
  mode: "column" | "cell";
  precomputedFunctionCalls: PrecomputedFunctionCall[];
}

export interface ExecuteBatchQueryRequest extends TabularQueryOptions {
  targets: TabularTarget[];
  precomputedFunctionCalls?: PrecomputedFunctionCall[];
}

export interface ExecuteSingleQueryRequest extends TabularQueryOptions {
  target: TabularTarget;
  precomputedFunctionCalls?: PrecomputedFunctionCall[];
}

export type TabularSortValue =
  | { numberValue: number }
  | { isoDatetimeValue: string };

export interface CitationNoteRecord {
  citationId: string;
  source?: string;
  document?: string;
  category?: string;
  company?: string;
  publishedDate?: string;
  ticker?: string;
  fqlFormula?: string;
  field?: string;
}

export interface ExecuteQueryResult {
  companyName: string;
  responseShort: string;
  typedValue?: JsonValue;
  sortValue?: TabularSortValue;
  citationIds: string[];
  citationNoteIndex: Record<string, CitationNoteRecord>;
}

export interface ExecuteBatchQueryResponse {
  results: ExecuteQueryResult[];
}

export type ExecuteCellBatchQueryRequest = ExecuteBatchQueryRequest;

export type ExecuteCellBatchFailureKind =
  | "auth"
  | "validation"
  | "timeout"
  | "rate_limit"
  | "upstream"
  | "network"
  | "contract";

export interface ExecuteCellBatchQueryFailure {
  targetIndex: number;
  kind: ExecuteCellBatchFailureKind;
  message: string;
  status?: number;
}

export interface ExecuteCellBatchQueryResponse
  extends ExecuteBatchQueryResponse {
  failures?: ExecuteCellBatchQueryFailure[];
}

export type TabularQueryErrorKind =
  | ExecuteCellBatchFailureKind
  | "disabled"
  | "aborted";

export type TabularOperationName =
  | "resolveDataFormat"
  | "getFunctionCallMetadata"
  | "executeBatchQuery"
  | "executeCellBatchQuery"
  | "executeSingleQuery";

export class TabularQueryExecutionError extends Error {
  readonly kind: TabularQueryErrorKind;
  readonly operation: TabularOperationName;
  readonly status?: number;

  constructor(
    kind: TabularQueryErrorKind,
    operation: TabularOperationName,
    message: string,
    status?: number
  ) {
    super(message);
    this.name = "TabularQueryExecutionError";
    this.kind = kind;
    this.operation = operation;
    this.status = status;
  }
}

type JsonObject = Record<string, unknown>;

interface RequestLifecycle {
  signal: AbortSignal;
  cleanup: () => void;
  didTimeOut: () => boolean;
}

function hasOwn(record: JsonObject, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function isRecord(value: unknown): value is JsonObject {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function validationError(
  operation: TabularOperationName,
  message: string
): TabularQueryExecutionError {
  return new TabularQueryExecutionError("validation", operation, message);
}

function contractError(
  operation: TabularOperationName,
  path: string
): TabularQueryExecutionError {
  return new TabularQueryExecutionError(
    "contract",
    operation,
    `Tabular query service returned an invalid response at ${path}.`,
    502
  );
}

function assertNoCallerIdentity(
  value: unknown,
  operation: TabularOperationName,
  seen = new WeakSet<object>()
): void {
  if (!value || typeof value !== "object") return;
  if (seen.has(value)) {
    throw validationError(operation, "Tabular query request must be JSON-serializable.");
  }
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item) => assertNoCallerIdentity(item, operation, seen));
    seen.delete(value);
    return;
  }

  for (const [key, nestedValue] of Object.entries(value)) {
    const normalizedKey = key.toLowerCase();
    if (normalizedKey === "user_id" || normalizedKey === "userid") {
      throw validationError(
        operation,
        "Caller-supplied user identity is not allowed."
      );
    }
    assertNoCallerIdentity(nestedValue, operation, seen);
  }
  seen.delete(value);
}

function requireString(
  value: unknown,
  operation: TabularOperationName,
  field: string,
  allowEmpty = false
): string {
  if (typeof value !== "string" || (!allowEmpty && !value.trim())) {
    throw validationError(operation, `${field} must be a non-empty string.`);
  }
  return value;
}

function serializeStringArray(
  value: string[] | undefined,
  operation: TabularOperationName,
  field: string
): string[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value)) {
    throw validationError(operation, `${field} must be an array of strings.`);
  }
  return value.map((item) => requireString(item, operation, field));
}

function assertJsonValue(
  value: unknown,
  operation: TabularOperationName,
  field: string,
  seen = new WeakSet<object>()
): asserts value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return;
  }
  if (typeof value === "number") {
    if (Number.isFinite(value)) return;
    throw validationError(operation, `${field} must contain finite JSON numbers.`);
  }
  if (!value || typeof value !== "object") {
    throw validationError(operation, `${field} must contain JSON values only.`);
  }
  if (seen.has(value)) {
    throw validationError(operation, `${field} must not contain cycles.`);
  }
  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach((item) => assertJsonValue(item, operation, field, seen));
  } else {
    Object.values(value).forEach((item) =>
      assertJsonValue(item, operation, field, seen)
    );
  }
  seen.delete(value);
}

function serializeDataFormat(
  dataFormat: TabularDataFormat,
  operation: TabularOperationName
): JsonObject {
  if (!isRecord(dataFormat)) {
    throw validationError(operation, "dataFormat must be an object.");
  }
  switch (dataFormat.formatType) {
    case "auto":
      return { format_type: "auto" };
    case "number":
      return {
        format_type: "number",
        ...(dataFormat.unit !== undefined ? { unit: dataFormat.unit } : {}),
        ...(dataFormat.decimalPlaces !== undefined
          ? { decimal_places: dataFormat.decimalPlaces }
          : {}),
      };
    case "currency":
      return {
        format_type: "currency",
        ...(dataFormat.currency !== undefined
          ? { currency: dataFormat.currency }
          : {}),
        ...(dataFormat.unit !== undefined ? { unit: dataFormat.unit } : {}),
        ...(dataFormat.decimalPlaces !== undefined
          ? { decimal_places: dataFormat.decimalPlaces }
          : {}),
      };
    case "percentage":
      return {
        format_type: "percentage",
        ...(dataFormat.decimalPlaces !== undefined
          ? { decimal_places: dataFormat.decimalPlaces }
          : {}),
      };
    case "date":
      return {
        format_type: "date",
        ...(dataFormat.dateFormat !== undefined
          ? { date_format: dataFormat.dateFormat }
          : {}),
      };
    case "template":
      return {
        format_type: "template",
        template: requireString(
          dataFormat.template,
          operation,
          "dataFormat.template"
        ),
      };
    default:
      throw validationError(operation, "dataFormat.formatType is not supported.");
  }
}

function serializeTarget(
  target: TabularTarget,
  operation: TabularOperationName
): JsonObject {
  if (!isRecord(target)) {
    throw validationError(operation, "target must be an object.");
  }
  const company = isRecord(target.company) ? target.company : null;
  const document = isRecord(target.document) ? target.document : null;
  if (Number(Boolean(company)) + Number(Boolean(document)) !== 1) {
    throw validationError(
      operation,
      "Exactly one company or document target is required."
    );
  }

  if (company) {
    return {
      company: {
        company_name: requireString(
          company.companyName,
          operation,
          "target.company.companyName"
        ),
        ...(company.companyId !== undefined
          ? {
              company_id: requireString(
                company.companyId,
                operation,
                "target.company.companyId",
                true
              ),
            }
          : {}),
        ...(company.companyTicker !== undefined
          ? {
              company_ticker: requireString(
                company.companyTicker,
                operation,
                "target.company.companyTicker",
                true
              ),
            }
          : {}),
        ...(company.companyIsin !== undefined
          ? {
              company_isin: requireString(
                company.companyIsin,
                operation,
                "target.company.companyIsin",
                true
              ),
            }
          : {}),
        ...(company.userFileIds !== undefined
          ? {
              user_file_ids: serializeStringArray(
                company.userFileIds as string[],
                operation,
                "target.company.userFileIds"
              ),
            }
          : {}),
        ...(company.directUserFileIds !== undefined
          ? {
              direct_user_file_ids: serializeStringArray(
                company.directUserFileIds as string[],
                operation,
                "target.company.directUserFileIds"
              ),
            }
          : {}),
      },
    };
  }

  return {
    document: {
      row_label: requireString(
        document?.rowLabel,
        operation,
        "target.document.rowLabel"
      ),
      ...(document?.userFileIds !== undefined
        ? {
            user_file_ids: serializeStringArray(
              document.userFileIds as string[],
              operation,
              "target.document.userFileIds"
            ),
          }
        : {}),
      ...(document?.directUserFileIds !== undefined
        ? {
            direct_user_file_ids: serializeStringArray(
              document.directUserFileIds as string[],
              operation,
              "target.document.directUserFileIds"
            ),
          }
        : {}),
    },
  };
}

function serializePrecomputedFunctionCall(
  call: PrecomputedFunctionCall,
  operation: TabularOperationName
): JsonObject {
  if (!isRecord(call) || !isRecord(call.functionParameters)) {
    throw validationError(
      operation,
      "precomputedFunctionCalls must contain valid function call objects."
    );
  }
  assertJsonValue(
    call.functionParameters,
    operation,
    "precomputedFunctionCalls.functionParameters"
  );
  if (
    call.stage !== undefined &&
    (!Number.isInteger(call.stage) || call.stage < 0 || call.stage > 2)
  ) {
    throw validationError(
      operation,
      "precomputedFunctionCalls.stage must be 0, 1, or 2."
    );
  }
  return {
    function_name: requireString(
      call.functionName,
      operation,
      "precomputedFunctionCalls.functionName"
    ),
    function_parameters: call.functionParameters,
    ...(call.functionCallId !== undefined
      ? {
          function_call_id: requireString(
            call.functionCallId,
            operation,
            "precomputedFunctionCalls.functionCallId"
          ),
        }
      : {}),
    ...(call.stage !== undefined ? { stage: call.stage } : {}),
  };
}

/**
 * Emit Skill guidance exactly as resolved, or not at all.
 *
 * Blank guidance is nothing to apply, and sending it would put an ordinary
 * request behind the Skills entitlement upstream, so it is omitted here the way
 * the server treats it.
 */
function serializeSelectedSkillsGuidance(
  request: SelectedSkillsGuidanceRequestField,
  operation: TabularOperationName
): JsonObject {
  if (request.selectedSkillsGuidance === undefined) return {};
  const guidance = requireString(
    request.selectedSkillsGuidance,
    operation,
    "selectedSkillsGuidance",
    true
  );
  return guidance.trim() ? { selected_skills_guidance: guidance } : {};
}

function serializeQueryOptions(
  request: TabularQueryOptions,
  operation: TabularOperationName
): JsonObject {
  const dataSources = serializeStringArray(
    request.dataSources,
    operation,
    "dataSources"
  );
  const tagOptions = serializeStringArray(
    request.tagOptions,
    operation,
    "tagOptions"
  );
  return {
    prompt: requireString(request.prompt, operation, "prompt"),
    response_type: request.responseType,
    ...(dataSources !== undefined ? { data_sources: dataSources } : {}),
    ...(tagOptions !== undefined ? { tag_options: tagOptions } : {}),
    ...(request.noMatchValue !== undefined
      ? {
          no_match_value: requireString(
            request.noMatchValue,
            operation,
            "noMatchValue",
            true
          ),
        }
      : {}),
    ...(request.dataFormat !== undefined
      ? { data_format: serializeDataFormat(request.dataFormat, operation) }
      : {}),
    ...serializeSelectedSkillsGuidance(request, operation),
  };
}

export function serializeResolveDataFormatRequest(
  request: ResolveDataFormatRequest
): JsonObject {
  const operation = "resolveDataFormat";
  assertNoCallerIdentity(request, operation);
  const dataSources = serializeStringArray(
    request.dataSources,
    operation,
    "dataSources"
  );
  return {
    prompt: requireString(request.prompt, operation, "prompt"),
    response_type: request.responseType,
    ...(dataSources !== undefined ? { data_sources: dataSources } : {}),
    ...serializeSelectedSkillsGuidance(request, operation),
  };
}

export function serializeGetFunctionCallMetadataRequest(
  request: GetFunctionCallMetadataRequest
): JsonObject {
  const operation = "getFunctionCallMetadata";
  assertNoCallerIdentity(request, operation);
  return {
    ...serializeQueryOptions(request, operation),
    targets: request.targets.map((target) => serializeTarget(target, operation)),
  };
}

export function serializeExecuteBatchQueryRequest(
  request: ExecuteBatchQueryRequest
): JsonObject {
  return serializeMultiTargetQueryRequest(request, "executeBatchQuery");
}

export function serializeExecuteCellBatchQueryRequest(
  request: ExecuteCellBatchQueryRequest
): JsonObject {
  return serializeMultiTargetQueryRequest(request, "executeCellBatchQuery");
}

function serializeMultiTargetQueryRequest(
  request: ExecuteBatchQueryRequest,
  operation: "executeBatchQuery" | "executeCellBatchQuery"
): JsonObject {
  assertNoCallerIdentity(request, operation);
  return {
    ...serializeQueryOptions(request, operation),
    targets: request.targets.map((target) => serializeTarget(target, operation)),
    ...(request.precomputedFunctionCalls !== undefined
      ? {
          precomputed_function_calls: request.precomputedFunctionCalls.map(
            (call) => serializePrecomputedFunctionCall(call, operation)
          ),
        }
      : {}),
  };
}

export function serializeExecuteSingleQueryRequest(
  request: ExecuteSingleQueryRequest
): JsonObject {
  const operation = "executeSingleQuery";
  assertNoCallerIdentity(request, operation);
  return {
    ...serializeQueryOptions(request, operation),
    target: serializeTarget(request.target, operation),
    ...(request.precomputedFunctionCalls !== undefined
      ? {
          precomputed_function_calls: request.precomputedFunctionCalls.map(
            (call) => serializePrecomputedFunctionCall(call, operation)
          ),
        }
      : {}),
  };
}

function expectRecord(
  value: unknown,
  operation: TabularOperationName,
  path: string
): JsonObject {
  if (!isRecord(value)) throw contractError(operation, path);
  return value;
}

function assertExactKeys(
  record: JsonObject,
  allowedKeys: readonly string[],
  operation: TabularOperationName,
  path: string
): void {
  const allowed = new Set(allowedKeys);
  if (Object.keys(record).some((key) => !allowed.has(key))) {
    throw contractError(operation, path);
  }
}

function expectResponseString(
  value: unknown,
  operation: TabularOperationName,
  path: string,
  allowEmpty = false
): string {
  if (
    typeof value !== "string" ||
    (!allowEmpty && value.trim().length === 0)
  ) {
    throw contractError(operation, path);
  }
  return value;
}

function expectOptionalResponseString(
  record: JsonObject,
  key: string,
  operation: TabularOperationName,
  path: string,
  allowEmpty = false
): string | undefined {
  if (!hasOwn(record, key)) return undefined;
  return expectResponseString(
    record[key],
    operation,
    `${path}.${key}`,
    allowEmpty
  );
}

function expectOptionalInteger(
  record: JsonObject,
  key: string,
  operation: TabularOperationName,
  path: string
): number | undefined {
  if (!hasOwn(record, key)) return undefined;
  const value = record[key];
  if (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value < 0 ||
    value > 6
  ) {
    throw contractError(operation, `${path}.${key}`);
  }
  return value;
}

function decodeDataFormat(
  value: unknown,
  operation: TabularOperationName,
  path: string
): TabularDataFormat {
  const record = expectRecord(value, operation, path);
  const formatType = record.format_type;
  switch (formatType) {
    case "auto":
      assertExactKeys(record, ["format_type"], operation, path);
      return { formatType: "auto" };
    case "number": {
      assertExactKeys(
        record,
        ["format_type", "unit", "decimal_places"],
        operation,
        path
      );
      const unit = expectOptionalResponseString(
        record,
        "unit",
        operation,
        path,
        true
      );
      const decimalPlaces = expectOptionalInteger(
        record,
        "decimal_places",
        operation,
        path
      );
      return {
        formatType: "number",
        ...(unit !== undefined ? { unit } : {}),
        ...(decimalPlaces !== undefined ? { decimalPlaces } : {}),
      };
    }
    case "currency": {
      assertExactKeys(
        record,
        ["format_type", "currency", "unit", "decimal_places"],
        operation,
        path
      );
      const currency = expectOptionalResponseString(
        record,
        "currency",
        operation,
        path,
        true
      );
      const unit = expectOptionalResponseString(
        record,
        "unit",
        operation,
        path,
        true
      );
      const decimalPlaces = expectOptionalInteger(
        record,
        "decimal_places",
        operation,
        path
      );
      return {
        formatType: "currency",
        ...(currency !== undefined ? { currency } : {}),
        ...(unit !== undefined ? { unit } : {}),
        ...(decimalPlaces !== undefined ? { decimalPlaces } : {}),
      };
    }
    case "percentage": {
      assertExactKeys(
        record,
        ["format_type", "decimal_places"],
        operation,
        path
      );
      const decimalPlaces = expectOptionalInteger(
        record,
        "decimal_places",
        operation,
        path
      );
      return {
        formatType: "percentage",
        ...(decimalPlaces !== undefined ? { decimalPlaces } : {}),
      };
    }
    case "date": {
      assertExactKeys(
        record,
        ["format_type", "date_format"],
        operation,
        path
      );
      const dateFormat = expectOptionalResponseString(
        record,
        "date_format",
        operation,
        path,
        true
      );
      return {
        formatType: "date",
        ...(dateFormat !== undefined ? { dateFormat } : {}),
      };
    }
    case "template":
      assertExactKeys(record, ["format_type", "template"], operation, path);
      return {
        formatType: "template",
        template: expectResponseString(
          record.template,
          operation,
          `${path}.template`
        ),
      };
    default:
      throw contractError(operation, `${path}.format_type`);
  }
}

function decodePrecomputedFunctionCall(
  value: unknown,
  operation: TabularOperationName,
  path: string
): PrecomputedFunctionCall {
  const record = expectRecord(value, operation, path);
  assertExactKeys(
    record,
    ["function_name", "function_parameters", "function_call_id", "stage"],
    operation,
    path
  );
  const functionParameters = expectRecord(
    record.function_parameters,
    operation,
    `${path}.function_parameters`
  );
  if (!isJsonValue(functionParameters)) {
    throw contractError(operation, `${path}.function_parameters`);
  }
  const functionCallId = expectOptionalResponseString(
    record,
    "function_call_id",
    operation,
    path
  );
  const stage = record.stage;
  if (
    stage !== undefined &&
    (typeof stage !== "number" ||
      !Number.isInteger(stage) ||
      stage < 0 ||
      stage > 2)
  ) {
    throw contractError(operation, `${path}.stage`);
  }
  return {
    functionName: expectResponseString(
      record.function_name,
      operation,
      `${path}.function_name`
    ),
    functionParameters: functionParameters as Record<string, JsonValue>,
    ...(functionCallId !== undefined ? { functionCallId } : {}),
    ...(stage !== undefined ? { stage: stage as 0 | 1 | 2 } : {}),
  };
}

function isJsonValue(
  value: unknown,
  seen = new WeakSet<object>()
): value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }
  if (typeof value === "number") return Number.isFinite(value);
  if (!value || typeof value !== "object" || seen.has(value)) return false;
  seen.add(value);
  const valid = Array.isArray(value)
    ? value.every((item) => isJsonValue(item, seen))
    : Object.values(value).every((item) => isJsonValue(item, seen));
  seen.delete(value);
  return valid;
}

function decodeSortValue(
  value: unknown,
  operation: TabularOperationName,
  path: string
): TabularSortValue {
  const record = expectRecord(value, operation, path);
  assertExactKeys(
    record,
    ["number_value", "iso_datetime_value"],
    operation,
    path
  );
  const hasNumber = hasOwn(record, "number_value");
  const hasDate = hasOwn(record, "iso_datetime_value");
  if (Number(hasNumber) + Number(hasDate) !== 1) {
    throw contractError(operation, path);
  }
  if (hasNumber) {
    if (
      typeof record.number_value !== "number" ||
      !Number.isFinite(record.number_value)
    ) {
      throw contractError(operation, `${path}.number_value`);
    }
    return { numberValue: record.number_value };
  }
  return {
    isoDatetimeValue: expectResponseString(
      record.iso_datetime_value,
      operation,
      `${path}.iso_datetime_value`
    ),
  };
}

function decodeCitationNoteRecord(
  value: unknown,
  operation: TabularOperationName,
  path: string
): CitationNoteRecord {
  const record = expectRecord(value, operation, path);
  assertExactKeys(
    record,
    [
      "citation_id",
      "source",
      "document",
      "category",
      "company",
      "published_date",
      "ticker",
      "fql_formula",
      "field",
    ],
    operation,
    path
  );
  const optionalFields = {
    source: expectOptionalResponseString(record, "source", operation, path),
    document: expectOptionalResponseString(record, "document", operation, path),
    category: expectOptionalResponseString(record, "category", operation, path),
    company: expectOptionalResponseString(record, "company", operation, path),
    publishedDate: expectOptionalResponseString(
      record,
      "published_date",
      operation,
      path
    ),
    ticker: expectOptionalResponseString(record, "ticker", operation, path),
    fqlFormula: expectOptionalResponseString(
      record,
      "fql_formula",
      operation,
      path
    ),
    field: expectOptionalResponseString(record, "field", operation, path),
  };
  return {
    citationId: expectResponseString(
      record.citation_id,
      operation,
      `${path}.citation_id`
    ),
    ...Object.fromEntries(
      Object.entries(optionalFields).filter(([, fieldValue]) => fieldValue !== undefined)
    ),
  };
}

function decodeQueryResult(
  value: unknown,
  operation: TabularOperationName,
  path: string
): ExecuteQueryResult {
  const record = expectRecord(value, operation, path);
  assertExactKeys(
    record,
    [
      "company_name",
      "response_short",
      "typed_value",
      "sort_value",
      "citation_ids",
      "citation_note_index",
    ],
    operation,
    path
  );
  if (!Array.isArray(record.citation_ids)) {
    throw contractError(operation, `${path}.citation_ids`);
  }
  if (record.citation_ids.length > MAX_CITATIONS_PER_RESULT) {
    throw contractError(operation, `${path}.citation_ids`);
  }
  const citationIds = record.citation_ids.map((citationId, index) =>
    expectResponseString(
      citationId,
      operation,
      `${path}.citation_ids[${index}]`
    )
  );
  const citationIdSet = new Set(citationIds);
  if (citationIdSet.size !== citationIds.length) {
    throw contractError(operation, `${path}.citation_ids`);
  }

  const rawCitationIndex = expectRecord(
    record.citation_note_index,
    operation,
    `${path}.citation_note_index`
  );
  if (Object.keys(rawCitationIndex).length > MAX_CITATIONS_PER_RESULT) {
    throw contractError(operation, `${path}.citation_note_index`);
  }
  const citationNoteEntries: Array<[string, CitationNoteRecord]> = [];
  for (const [citationId, rawRecord] of Object.entries(rawCitationIndex)) {
    const decoded = decodeCitationNoteRecord(
      rawRecord,
      operation,
      `${path}.citation_note_index.${citationId}`
    );
    if (
      decoded.citationId !== citationId ||
      !citationIdSet.has(citationId)
    ) {
      throw contractError(operation, `${path}.citation_note_index.${citationId}`);
    }
    citationNoteEntries.push([citationId, decoded]);
  }
  const citationNoteIndex = Object.fromEntries(citationNoteEntries);

  let typedValue: JsonValue | undefined;
  if (hasOwn(record, "typed_value")) {
    if (!isJsonValue(record.typed_value)) {
      throw contractError(operation, `${path}.typed_value`);
    }
    typedValue = record.typed_value;
  }
  const sortValue = hasOwn(record, "sort_value")
    ? decodeSortValue(record.sort_value, operation, `${path}.sort_value`)
    : undefined;

  return {
    companyName: expectResponseString(
      record.company_name,
      operation,
      `${path}.company_name`
    ),
    responseShort: expectResponseString(
      record.response_short,
      operation,
      `${path}.response_short`,
      true
    ),
    ...(typedValue !== undefined ? { typedValue } : {}),
    ...(sortValue !== undefined ? { sortValue } : {}),
    citationIds,
    citationNoteIndex,
  };
}

function decodeResolveDataFormatResponse(
  value: unknown
): ResolveDataFormatResponse {
  const operation = "resolveDataFormat";
  const record = expectRecord(value, operation, "response");
  assertExactKeys(
    record,
    ["data_format", "cleaned_prompt"],
    operation,
    "response"
  );
  const cleanedPrompt = expectOptionalResponseString(
    record,
    "cleaned_prompt",
    operation,
    "response",
    true
  );
  return {
    dataFormat: decodeDataFormat(
      record.data_format,
      operation,
      "response.data_format"
    ),
    ...(cleanedPrompt !== undefined ? { cleanedPrompt } : {}),
  };
}

function decodeGetFunctionCallMetadataResponse(
  value: unknown
): GetFunctionCallMetadataResponse {
  const operation = "getFunctionCallMetadata";
  const record = expectRecord(value, operation, "response");
  assertExactKeys(
    record,
    ["mode", "precomputed_function_calls"],
    operation,
    "response"
  );
  if (record.mode !== "column" && record.mode !== "cell") {
    throw contractError(operation, "response.mode");
  }
  if (
    !Array.isArray(record.precomputed_function_calls) ||
    record.precomputed_function_calls.length > MAX_PRECOMPUTED_FUNCTION_CALLS
  ) {
    throw contractError(operation, "response.precomputed_function_calls");
  }
  return {
    mode: record.mode,
    precomputedFunctionCalls: record.precomputed_function_calls.map(
      (call, index) =>
        decodePrecomputedFunctionCall(
          call,
          operation,
          `response.precomputed_function_calls[${index}]`
        )
    ),
  };
}

function decodeMultiTargetQueryResponse(
  value: unknown,
  operation: "executeBatchQuery" | "executeCellBatchQuery",
  maxResults: number
): ExecuteBatchQueryResponse {
  const record = expectRecord(value, operation, "response");
  assertExactKeys(record, ["results"], operation, "response");
  if (
    !Array.isArray(record.results) ||
    record.results.length > maxResults
  ) {
    throw contractError(operation, "response.results");
  }
  return {
    results: record.results.map((result, index) =>
      decodeQueryResult(result, operation, `response.results[${index}]`)
    ),
  };
}

function decodeExecuteBatchQueryResponse(
  value: unknown
): ExecuteBatchQueryResponse {
  return decodeMultiTargetQueryResponse(
    value,
    "executeBatchQuery",
    MAX_BATCH_RESULTS
  );
}

function decodeExecuteCellBatchQueryResponse(
  value: unknown
): ExecuteCellBatchQueryResponse {
  const operation = "executeCellBatchQuery";
  const record = expectRecord(value, operation, "response");
  assertExactKeys(record, ["results", "failures"], operation, "response");
  if (
    !Array.isArray(record.results) ||
    record.results.length > MAX_CELL_BATCH_RESULTS
  ) {
    throw contractError(operation, "response.results");
  }
  const rawFailures = hasOwn(record, "failures") ? record.failures : [];
  if (
    !Array.isArray(rawFailures) ||
    rawFailures.length > MAX_CELL_BATCH_RESULTS
  ) {
    throw contractError(operation, "response.failures");
  }
  const allowedKinds = new Set<ExecuteCellBatchFailureKind>([
    "auth",
    "validation",
    "timeout",
    "rate_limit",
    "upstream",
    "network",
    "contract",
  ]);
  const seenTargetIndexes = new Set<number>();
  const failures = rawFailures.map((failure, index) => {
    const path = `response.failures[${index}]`;
    const failureRecord = expectRecord(failure, operation, path);
    assertExactKeys(
      failureRecord,
      ["target_index", "kind", "message", "status"],
      operation,
      path
    );
    const targetIndex = failureRecord.target_index;
    const kind = failureRecord.kind;
    const status = failureRecord.status;
    if (
      typeof targetIndex !== "number" ||
      !Number.isInteger(targetIndex) ||
      targetIndex < 0 ||
      targetIndex >= MAX_CELL_BATCH_RESULTS ||
      seenTargetIndexes.has(targetIndex)
    ) {
      throw contractError(operation, `${path}.target_index`);
    }
    if (
      typeof kind !== "string" ||
      !allowedKinds.has(kind as ExecuteCellBatchFailureKind)
    ) {
      throw contractError(operation, `${path}.kind`);
    }
    if (
      status !== undefined &&
      (typeof status !== "number" ||
        !Number.isInteger(status) ||
        status < 400 ||
        status > 599)
    ) {
      throw contractError(operation, `${path}.status`);
    }
    seenTargetIndexes.add(targetIndex);
    return {
      targetIndex,
      kind: kind as ExecuteCellBatchFailureKind,
      message: expectResponseString(
        failureRecord.message,
        operation,
        `${path}.message`
      ),
      ...(typeof status === "number" ? { status } : {}),
    };
  });
  return {
    results: record.results.map((result, index) =>
      decodeQueryResult(result, operation, `response.results[${index}]`)
    ),
    ...(failures.length > 0 ? { failures } : {}),
  };
}

function decodeExecuteSingleQueryResponse(value: unknown): ExecuteQueryResult {
  return decodeQueryResult(value, "executeSingleQuery", "response");
}

function createRequestLifecycle(
  userSignal: AbortSignal | undefined,
  timeoutMs: number
): RequestLifecycle {
  const controller = new AbortController();
  let timedOut = false;
  const onUserAbort = () => controller.abort();
  if (userSignal?.aborted) {
    controller.abort();
  } else {
    userSignal?.addEventListener("abort", onUserAbort, { once: true });
  }
  const timeoutId = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    didTimeOut: () => timedOut,
    cleanup: () => {
      globalThis.clearTimeout(timeoutId);
      userSignal?.removeEventListener("abort", onUserAbort);
    },
  };
}

function parseResponseText(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function readErrorMessage(payload: unknown, fallback: string): string {
  if (isRecord(payload)) {
    if (typeof payload.message === "string" && payload.message.trim()) {
      return payload.message.trim();
    }
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error.trim();
    }
  }
  return fallback;
}

function mapHttpError(
  operation: TabularOperationName,
  response: Response,
  payload: unknown
): TabularQueryExecutionError {
  const fallback = `Tabular query request failed with HTTP ${response.status}.`;
  const message = readErrorMessage(payload, fallback);
  if (response.status === 401 || response.status === 403) {
    return new TabularQueryExecutionError(
      "auth",
      operation,
      message,
      response.status
    );
  }
  if (
    response.status === 400 ||
    response.status === 413 ||
    response.status === 422
  ) {
    return new TabularQueryExecutionError(
      "validation",
      operation,
      message,
      response.status
    );
  }
  if (response.status === 408 || response.status === 504) {
    return new TabularQueryExecutionError(
      "timeout",
      operation,
      message,
      response.status
    );
  }
  if (response.status === 404) {
    return new TabularQueryExecutionError(
      "disabled",
      operation,
      message,
      response.status
    );
  }
  if (response.status === 429) {
    return new TabularQueryExecutionError(
      "rate_limit",
      operation,
      message,
      response.status
    );
  }
  return new TabularQueryExecutionError(
    "upstream",
    operation,
    message,
    response.status
  );
}

async function requestTabularOperation<T>(
  operation: TabularOperationName,
  path: string,
  requestBody: JsonObject,
  decode: (value: unknown) => T,
  timeoutMs: number,
  userSignal?: AbortSignal,
  langfuseMetadata?: Record<string, unknown>
): Promise<T> {
  const lifecycle = createRequestLifecycle(userSignal, timeoutMs);
  try {
    // Execution has no idempotency key, so this intentionally uses apiFetch
    // directly rather than the shared retrying fetch wrapper.
    const response = await apiFetch(
      `${TABULAR_QUERY_BASE_PATH}/${path}`,
      withLangfuseMetadataHeader(
        {
          method: "POST",
          headers: {
            "X-Finster-Office-Host": "excel",
          },
          body: JSON.stringify(requestBody),
          cache: "no-store",
          redirect: "error",
          signal: lifecycle.signal,
        },
        langfuseMetadata,
      ),
    );
    checkAuthStatus(response);
    const text = await response.text();
    const payload = parseResponseText(text);
    if (!response.ok) {
      throw mapHttpError(operation, response, payload);
    }
    if (payload === undefined) {
      throw contractError(operation, "response");
    }
    return decode(payload);
  } catch (error) {
    if (error instanceof TabularQueryExecutionError) throw error;
    if (lifecycle.didTimeOut()) {
      throw new TabularQueryExecutionError(
        "timeout",
        operation,
        "Tabular query operation timed out."
      );
    }
    if (userSignal?.aborted || isAbortError(error)) {
      throw new TabularQueryExecutionError(
        "aborted",
        operation,
        "Tabular query operation was cancelled."
      );
    }
    throw new TabularQueryExecutionError(
      "network",
      operation,
      "Tabular query service is unavailable right now."
    );
  } finally {
    lifecycle.cleanup();
  }
}

export async function resolveDataFormat(
  request: ResolveDataFormatRequest,
  signal?: AbortSignal,
  langfuseMetadata?: Record<string, unknown>
): Promise<ResolveDataFormatResponse> {
  return requestTabularOperation(
    "resolveDataFormat",
    "resolve-data-format",
    serializeResolveDataFormatRequest(request),
    decodeResolveDataFormatResponse,
    TABULAR_METADATA_REQUEST_TIMEOUT_MS,
    signal,
    langfuseMetadata
  );
}

export async function getFunctionCallMetadata(
  request: GetFunctionCallMetadataRequest,
  signal?: AbortSignal,
  langfuseMetadata?: Record<string, unknown>
): Promise<GetFunctionCallMetadataResponse> {
  return requestTabularOperation(
    "getFunctionCallMetadata",
    "get-function-call-metadata",
    serializeGetFunctionCallMetadataRequest(request),
    decodeGetFunctionCallMetadataResponse,
    TABULAR_METADATA_REQUEST_TIMEOUT_MS,
    signal,
    langfuseMetadata
  );
}

export async function executeBatchQuery(
  request: ExecuteBatchQueryRequest,
  signal?: AbortSignal,
  langfuseMetadata?: Record<string, unknown>
): Promise<ExecuteBatchQueryResponse> {
  return requestTabularOperation(
    "executeBatchQuery",
    "execute-batch-query",
    serializeExecuteBatchQueryRequest(request),
    decodeExecuteBatchQueryResponse,
    TABULAR_EXECUTION_REQUEST_TIMEOUT_MS,
    signal,
    langfuseMetadata
  );
}

export async function executeCellBatchQuery(
  request: ExecuteCellBatchQueryRequest,
  signal?: AbortSignal,
  langfuseMetadata?: Record<string, unknown>
): Promise<ExecuteCellBatchQueryResponse> {
  const response = await requestTabularOperation(
    "executeCellBatchQuery",
    "execute-cell-batch-query",
    serializeExecuteCellBatchQueryRequest(request),
    decodeExecuteCellBatchQueryResponse,
    TABULAR_EXECUTION_REQUEST_TIMEOUT_MS,
    signal,
    langfuseMetadata
  );
  const failures = response.failures ?? [];
  if (
    response.results.length + failures.length !== request.targets.length ||
    failures.some((failure) => failure.targetIndex >= request.targets.length)
  ) {
    throw contractError("executeCellBatchQuery", "response");
  }
  return response;
}

export async function executeSingleQuery(
  request: ExecuteSingleQueryRequest,
  signal?: AbortSignal,
  langfuseMetadata?: Record<string, unknown>
): Promise<ExecuteQueryResult> {
  return requestTabularOperation(
    "executeSingleQuery",
    "execute-single-query",
    serializeExecuteSingleQueryRequest(request),
    decodeExecuteSingleQueryResponse,
    TABULAR_EXECUTION_REQUEST_TIMEOUT_MS,
    signal,
    langfuseMetadata
  );
}
